(function () {
  function createCoveoRangeUiStateModule(deps) {
    const roundPriceToStep =
      typeof deps?.roundPriceToStep === "function"
        ? deps.roundPriceToStep
        : function (value) {
          return Number(value);
        };
    const getActiveCoveoLocationContext =
      typeof deps?.getActiveCoveoLocationContext === "function"
        ? deps.getActiveCoveoLocationContext
        : function () {
          return null;
        };
    const buildCoveoFacetValueCacheContextKey =
      typeof deps?.buildCoveoFacetValueCacheContextKey === "function"
        ? deps.buildCoveoFacetValueCacheContextKey
        : null;

    function syncCoveoRangeUiStateLegacyGlobals(store) {
      if (!store) return;
      window.__coveoPendingRangeUi = store.pending;
      window.__coveoLiveRangeUi = store.live;
      window.__coveoStickyRangeUi = store.sticky;
      window.__coveoFrozenRangeUi = store.frozen;
      window.__coveoStableRangeDomains = store.stableDomains;
    }

    function getCoveoRangeUiStateStore(createIfMissing) {
      if (!window.__coveoRangeUiStateStore && createIfMissing) {
        window.__coveoRangeUiStateStore = {
          pending:
            window.__coveoPendingRangeUi &&
              typeof window.__coveoPendingRangeUi === "object"
              ? window.__coveoPendingRangeUi
              : {},
          live:
            window.__coveoLiveRangeUi &&
              typeof window.__coveoLiveRangeUi === "object"
              ? window.__coveoLiveRangeUi
              : {},
          sticky:
            window.__coveoStickyRangeUi &&
              typeof window.__coveoStickyRangeUi === "object"
              ? window.__coveoStickyRangeUi
              : {},
          frozen:
            window.__coveoFrozenRangeUi &&
              typeof window.__coveoFrozenRangeUi === "object"
              ? window.__coveoFrozenRangeUi
              : {},
          stableDomains:
            window.__coveoStableRangeDomains &&
              typeof window.__coveoStableRangeDomains === "object"
              ? window.__coveoStableRangeDomains
              : {},
          interaction: null,
          deferredRender: null,
        };
        syncCoveoRangeUiStateLegacyGlobals(window.__coveoRangeUiStateStore);
      }
      return window.__coveoRangeUiStateStore || null;
    }

    function getCoveoPendingRangeUiMap(createIfMissing) {
      const store = getCoveoRangeUiStateStore(createIfMissing);
      return store ? store.pending : null;
    }

    function hasCoveoPendingRangeUiSnapshots() {
      const map = getCoveoPendingRangeUiMap(false);
      return !!(map && Object.keys(map).length > 0);
    }

    function getCoveoPendingRangeUiRawSnapshot(facetId) {
      if (!facetId) return null;
      const map = getCoveoPendingRangeUiMap(false);
      if (!map) return null;
      return map[String(facetId)] || null;
    }

    function normalizeCoveoFacetRangeSelectionCandidate(sel, domain, kind) {
      if (!sel || typeof sel !== "object") return null;
      if (sel.fullRange) return { fullRange: true };

      let lo = Number(sel.lo);
      let hi = Number(sel.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      if (kind === "length" || kind === "year" || kind === "numeric") {
        lo = Math.round(lo);
        hi = Math.round(hi);
      } else if (kind === "price") {
        lo = roundPriceToStep(lo, "round");
        hi = roundPriceToStep(hi, "round");
      }
      if (lo > hi) {
        const t = lo;
        lo = hi;
        hi = t;
      }

      const domLo = Number(domain?.lo);
      const domHi = Number(domain?.hi);
      if (Number.isFinite(domLo) && Number.isFinite(domHi)) {
        const minDom = Math.min(domLo, domHi);
        const maxDom = Math.max(domLo, domHi);
        lo = Math.max(minDom, Math.min(maxDom, lo));
        hi = Math.max(minDom, Math.min(maxDom, hi));
        const span = Math.max(maxDom - minDom, 0);
        const eps = span > 1e-9 ? span * 1e-6 : 1e-4;
        if (lo <= minDom + eps && hi >= maxDom - eps) {
          return { fullRange: true };
        }
      }

      return { lo: Math.min(lo, hi), hi: Math.max(lo, hi), fullRange: false };
    }

    function normalizeCoveoYearSelectionCandidate(sel, domain) {
      if (!sel || typeof sel !== "object") return null;
      if (sel.fullRange) return { fullRange: true };

      let lo = Math.round(Number(sel.lo));
      let hi = Math.round(Number(sel.hi));
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      if (lo > hi) {
        const t = lo;
        lo = hi;
        hi = t;
      }

      const domLo = Math.round(Number(domain?.lo));
      const domHi = Math.round(Number(domain?.hi));
      if (Number.isFinite(domLo) && Number.isFinite(domHi)) {
        const minDom = Math.min(domLo, domHi);
        const maxDom = Math.max(domLo, domHi);
        lo = Math.max(minDom, Math.min(maxDom, lo));
        hi = Math.max(minDom, Math.min(maxDom, hi));
        if (lo <= minDom && hi >= maxDom) {
          return { fullRange: true };
        }
      }

      return { lo, hi, fullRange: false };
    }

    function setCoveoPendingRangeUiSnapshot(facetId, kind, selection, domain) {
      if (!facetId || !kind || !selection) return null;
      const normalized = kind === "year"
        ? normalizeCoveoYearSelectionCandidate(selection, domain)
        : normalizeCoveoFacetRangeSelectionCandidate(selection, domain, kind);
      const map = getCoveoPendingRangeUiMap(true);
      const key = String(facetId);
      if (!normalized || normalized.fullRange) {
        delete map[key];
        return normalized;
      }
      map[key] = {
        kind: kind,
        lo: normalized.lo,
        hi: normalized.hi,
        rangeLo: Number.isFinite(Number(domain?.lo)) ? Number(domain.lo) : null,
        rangeHi: Number.isFinite(Number(domain?.hi)) ? Number(domain.hi) : null,
      };
      return normalized;
    }

    function getCoveoPendingRangeUiSnapshot(facetId, kind, domain) {
      const pending = getCoveoPendingRangeUiRawSnapshot(facetId);
      if (!pending) return null;
      if (!kind) return pending;
      if (pending.kind !== kind) return null;
      return kind === "year"
        ? normalizeCoveoYearSelectionCandidate(pending, domain)
        : normalizeCoveoFacetRangeSelectionCandidate(pending, domain, kind);
    }

    function clearCoveoPendingRangeUiSnapshot(facetId, options) {
      if (!facetId) return;
      const key = String(facetId);
      const store = getCoveoRangeUiStateStore(false);
      const pendingMap = getCoveoPendingRangeUiMap(false);
      if (pendingMap) delete pendingMap[key];
      if (!store) return;
      if (!options?.preserveLive) delete store.live[key];
      if (!options?.preserveSticky) delete store.sticky[key];
      if (!options?.preserveFrozen) delete store.frozen[key];
    }

    function clearAllCoveoPendingRangeUiSnapshots(options) {
      const store = getCoveoRangeUiStateStore(true);
      store.pending = {};
      if (!options?.preserveLive) store.live = {};
      if (!options?.preserveSticky) store.sticky = {};
      if (!options?.preserveFrozen) store.frozen = {};
      syncCoveoRangeUiStateLegacyGlobals(store);
    }

    function buildCoveoStableRangeDomainContextKey(facetId) {
      const req =
        window.__coveoLastCommerceRequest ||
        window.__coveoLastPatchedCommerceRequest ||
        null;
      const locCtx = getActiveCoveoLocationContext() || null;
      const body = req?.body && typeof req.body === "object" ? req.body : null;
      const canonicalRequestContext =
        body && buildCoveoFacetValueCacheContextKey
          ? buildCoveoFacetValueCacheContextKey(body, {
            excludeFacetId: facetId,
            requestUrl: req?.url || "",
          })
          : null;
      const selectedRadius =
        window.__coveoSelectedRadiusMiles == null
          ? NaN
          : Number(window.__coveoSelectedRadiusMiles);
      const contextRadius = Number(locCtx?.location_radius_miles);
      return JSON.stringify({
        page: typeof location !== "undefined" ? location.pathname : "",
        request: canonicalRequestContext,
        query: String(body?.query ?? req?.query ?? "").trim(),
        radius:
          Number.isFinite(selectedRadius)
            ? selectedRadius
            : Number.isFinite(contextRadius)
              ? contextRadius
              : null,
        location_type: String(locCtx?.location_type || "").trim(),
        location_zip: String(locCtx?.location_zip || "").trim(),
        location_lat: Number.isFinite(Number(locCtx?.location_lat))
          ? Number(locCtx.location_lat)
          : null,
        location_lng: Number.isFinite(Number(locCtx?.location_lng))
          ? Number(locCtx.location_lng)
          : null,
      });
    }

    function getCoveoStableRangeDomain(facetId, kind) {
      const store = getCoveoRangeUiStateStore(false);
      if (!facetId || !kind || !store) return null;
      const ctxKey = buildCoveoStableRangeDomainContextKey(facetId);
      const key = `${ctxKey}::${String(kind)}::${String(facetId)}`;
      const stored = store.stableDomains[key];
      if (!stored) return null;
      const lo = Number(stored.lo);
      const hi = Number(stored.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
      return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
    }

    function rememberCoveoStableRangeDomain(facetId, kind, domain) {
      if (!facetId || !kind || !domain) return null;
      const lo = Number(domain.lo);
      const hi = Number(domain.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
      const store = getCoveoRangeUiStateStore(true);
      const ctxKey = buildCoveoStableRangeDomainContextKey(facetId);
      const key = `${ctxKey}::${String(kind)}::${String(facetId)}`;
      const prev = store.stableDomains[key];
      const prevLo = Number(prev?.lo);
      const prevHi = Number(prev?.hi);
      const merged = {
        lo:
          Number.isFinite(prevLo) && Number.isFinite(prevHi)
            ? Math.min(prevLo, lo)
            : lo,
        hi:
          Number.isFinite(prevLo) && Number.isFinite(prevHi)
            ? Math.max(prevHi, hi)
            : hi,
      };
      store.stableDomains[key] = merged;
      return merged;
    }

    function isCoveoRangeUiFrozen(facetId) {
      const store = getCoveoRangeUiStateStore(false);
      if (!facetId || !store) return false;
      const until = Number(store.frozen[String(facetId)] || 0);
      if (!(until > Date.now())) {
        delete store.frozen[String(facetId)];
        return false;
      }
      return true;
    }

    function setCoveoRangeUiFrozen(facetId, durationMs) {
      if (!facetId) return 0;
      const store = getCoveoRangeUiStateStore(true);
      const until = Date.now() + Math.max(0, Number(durationMs) || 0);
      store.frozen[String(facetId)] = until;
      return until;
    }

    function getCoveoLiveRangeUiSnapshot(facetId, kind, domain) {
      const store = getCoveoRangeUiStateStore(false);
      if (!facetId || !store) return null;
      const live = store.live[String(facetId)];
      if (!live || live.kind !== kind) return null;
      return kind === "year"
        ? normalizeCoveoYearSelectionCandidate(live, domain)
        : normalizeCoveoFacetRangeSelectionCandidate(live, domain, kind);
    }

    function getCoveoStickyRangeUiSnapshot(facetId, kind, domain) {
      const store = getCoveoRangeUiStateStore(false);
      if (!facetId || !store) return null;
      const sticky = store.sticky[String(facetId)];
      if (!sticky || sticky.kind !== kind) return null;
      const expiresAt = Number(sticky.expiresAt || 0);
      if (expiresAt > 0 && expiresAt < Date.now()) {
        delete store.sticky[String(facetId)];
        return null;
      }
      return kind === "year"
        ? normalizeCoveoYearSelectionCandidate(sticky, domain)
        : normalizeCoveoFacetRangeSelectionCandidate(sticky, domain, kind);
    }

    function setCoveoLiveRangeUiSnapshot(facetId, kind, selection) {
      if (!facetId || !kind || !selection) return;
      const store = getCoveoRangeUiStateStore(true);
      if (selection.fullRange) {
        store.live[String(facetId)] = {
          kind: kind,
          fullRange: true,
        };
        return;
      }
      const lo = Number(selection.lo);
      const hi = Number(selection.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
      store.live[String(facetId)] = {
        kind: kind,
        lo: Math.min(lo, hi),
        hi: Math.max(lo, hi),
      };
    }

    function setCoveoStickyRangeUiSnapshot(facetId, kind, selection, ttlMs) {
      if (!facetId || !kind || !selection) return;
      const store = getCoveoRangeUiStateStore(true);
      const expiresAt = Date.now() + Math.max(0, Number(ttlMs) || 1200);
      if (selection.fullRange) {
        store.sticky[String(facetId)] = {
          kind: kind,
          fullRange: true,
          expiresAt,
        };
        return;
      }
      const lo = Number(selection.lo);
      const hi = Number(selection.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
      store.sticky[String(facetId)] = {
        kind: kind,
        lo: Math.min(lo, hi),
        hi: Math.max(lo, hi),
        expiresAt,
      };
    }

    function beginCoveoRangeUiInteraction(facetId, pointerId) {
      if (!facetId || pointerId == null) return false;
      const store = getCoveoRangeUiStateStore(true);
      store.interaction = {
        facetId: String(facetId),
        pointerId,
      };
      store.deferredRender = null;
      return true;
    }

    function hasActiveCoveoRangeUiInteraction() {
      const store = getCoveoRangeUiStateStore(false);
      return !!store?.interaction;
    }

    function deferCoveoRangeUiRender(renderPayload) {
      const store = getCoveoRangeUiStateStore(false);
      if (!store?.interaction) return false;
      store.deferredRender = renderPayload;
      return true;
    }

    function finishCoveoRangeUiInteraction(pointerId) {
      const store = getCoveoRangeUiStateStore(false);
      if (!store?.interaction || store.interaction.pointerId !== pointerId) {
        return null;
      }
      const deferred = store.deferredRender || null;
      store.interaction = null;
      store.deferredRender = null;
      return deferred;
    }

    function cancelCoveoRangeUiInteraction(pointerId) {
      const store = getCoveoRangeUiStateStore(false);
      if (
        !store?.interaction ||
        (pointerId != null && store.interaction.pointerId !== pointerId)
      ) {
        return false;
      }
      store.interaction = null;
      store.deferredRender = null;
      return true;
    }

    function syncCoveoPendingRangeUiSelectionToDomain(facetId, kind, domain) {
      if (!facetId) return null;
      const pending = getCoveoPendingRangeUiRawSnapshot(facetId);
      if (!pending || pending.kind !== kind) return null;
      const normalized = kind === "year"
        ? normalizeCoveoYearSelectionCandidate(pending, domain)
        : normalizeCoveoFacetRangeSelectionCandidate(pending, domain, kind);
      if (!normalized || normalized.fullRange) {
        const pendingMap = getCoveoPendingRangeUiMap(false);
        if (pendingMap) delete pendingMap[String(facetId)];
        return normalized;
      }
      setCoveoPendingRangeUiSnapshot(facetId, kind, normalized, domain);
      return normalized;
    }

    return {
      beginCoveoRangeUiInteraction,
      cancelCoveoRangeUiInteraction,
      clearAllCoveoPendingRangeUiSnapshots,
      clearCoveoPendingRangeUiSnapshot,
      deferCoveoRangeUiRender,
      finishCoveoRangeUiInteraction,
      getCoveoLiveRangeUiSnapshot,
      getCoveoPendingRangeUiMap,
      getCoveoPendingRangeUiRawSnapshot,
      getCoveoPendingRangeUiSnapshot,
      getCoveoRangeUiStateStore,
      getCoveoStableRangeDomain,
      getCoveoStickyRangeUiSnapshot,
      hasActiveCoveoRangeUiInteraction,
      hasCoveoPendingRangeUiSnapshots,
      isCoveoRangeUiFrozen,
      normalizeCoveoFacetRangeSelectionCandidate,
      normalizeCoveoYearSelectionCandidate,
      rememberCoveoStableRangeDomain,
      setCoveoLiveRangeUiSnapshot,
      setCoveoPendingRangeUiSnapshot,
      setCoveoRangeUiFrozen,
      setCoveoStickyRangeUiSnapshot,
      syncCoveoPendingRangeUiSelectionToDomain,
      syncCoveoRangeUiStateLegacyGlobals,
    };
  }

  window.CoveoRangeUiStateModule = {
    create: createCoveoRangeUiStateModule,
  };
})();
